import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { getStaffFirstPage } from './utils/staffUtils';
import RealtimeToast from './components/common/RealtimeToast';
import OfflineBanner  from './components/common/OfflineBanner';
import { AppSettingsProvider } from './context/AppSettingsContext';
import { SocketProvider } from './context/SocketContext';
import { CallProvider } from './context/CallContext';

// ── Patient pages ──────────────────────────────────────────────────────────
import HomePage           from './pages/patient/HomePage';
import SearchPage         from './pages/patient/SearchPage';
import DoctorProfilePage  from './pages/patient/DoctorProfilePage';
import BookingPage        from './pages/patient/BookingPage';
import BookingSuccessPage from './pages/patient/BookingSuccessPage';
import PaymentReturnPage from './pages/patient/PaymentReturnPage';
import MyAppointmentsPage from './pages/patient/MyAppointmentsPage';
import ChatListPage       from './pages/patient/ChatListPage';
import ChatPage           from './pages/patient/ChatPage';
import MedicineChatPage   from './pages/patient/MedicineChatPage';
import PatientLoginPage   from './pages/patient/PatientLoginPage';
import ProfileSetupPage   from './pages/patient/ProfileSetupPage';
import QRScannerPage          from './pages/patient/QRScannerPage';
import AppointmentDetailPage  from './pages/patient/AppointmentDetailPage';

// ── Doctor pages ───────────────────────────────────────────────────────────
import DoctorLoginPage             from './pages/doctor/DoctorLoginPage';
import DoctorDashboard             from './pages/doctor/DoctorDashboard';
import DoctorAppointments          from './pages/doctor/DoctorAppointments';
import DoctorProfileSetup          from './pages/doctor/DoctorProfileSetup';
import DoctorEarnings              from './pages/doctor/DoctorEarnings';
import DoctorChatList              from './pages/doctor/DoctorChatList';
import DoctorChatPage              from './pages/doctor/DoctorChatPage';
import DoctorStaff                 from './pages/doctor/DoctorStaff';
import DoctorMedicineChatPage      from './pages/doctor/DoctorMedicineChatPage';
import DoctorMedicineStoreChatList from './pages/doctor/DoctorMedicineStoreChatList';
import DoctorSlots                 from './pages/doctor/DoctorSlots';
import DoctorQRPage                from './pages/doctor/DoctorQRPage';

// ── Admin pages ────────────────────────────────────────────────────────────
import AdminLoginPage    from './pages/admin/AdminLoginPage';
import AdminDashboard    from './pages/admin/AdminDashboard';
import AdminPayments     from './pages/admin/AdminPayments';
import AdminDoctors      from './pages/admin/AdminDoctors';
import AdminPatients     from './pages/admin/AdminPatients';
import AdminAppointments from './pages/admin/AdminAppointments';
import AdminSettings     from './pages/admin/AdminSettings';
import AdminRevenue      from './pages/admin/AdminRevenue';
import AdminStaffPage    from './pages/admin/AdminStaffPage';
import GlobalCallOverlay    from './components/common/GlobalCallOverlay';
import SplashScreen         from './components/common/SplashScreen';

// ── Full-screen loading spinner ────────────────────────────────────────────
function AppLoader() {
  // Only show if not already showing SplashScreen (avoid double spinner)
  const splashShown = sessionStorage.getItem('splashShown');
  if (!splashShown) return null; // SplashScreen is showing — don't double up
  return (
    <div className="fixed inset-0 bg-white flex flex-col items-center justify-center z-50">
      <div className="w-12 h-12 border-4 border-primary-light border-t-primary rounded-full animate-spin mb-4" />
      <p className="text-sm text-text-muted font-medium">Loading...</p>
    </div>
  );
}

// ── Route guards — wait for hydration before deciding ─────────────────────

function PatientRoute({ children }) {
  const { isLoggedIn, role, isHydrated } = useAuth();
  const location = useLocation();
  if (!isHydrated) return <AppLoader />;
  if (!isLoggedIn) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  // Admin should not access patient pages
  if (role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  // NOTE: doctors/staff CAN visit booking pages — do NOT redirect them
  return children;
}

// Strict patient-only route (chat, appointments list etc — not booking)
function PatientOnlyRoute({ children }) {
  const { isLoggedIn, role, isHydrated } = useAuth();
  const location = useLocation();
  if (!isHydrated) return <AppLoader />;
  if (!isLoggedIn) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (role === 'doctor' || role === 'staff') return <Navigate to="/doctor/dashboard" replace />;
  if (role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { isLoggedIn, role, user, isProfileComplete, isHydrated } = useAuth();
  if (!isHydrated) return <AppLoader />;
  // Redirect any logged-in user away from the patient login page
  if (isLoggedIn) {
    if (role === 'patient') return <Navigate to={isProfileComplete ? '/' : '/setup-profile'} replace />;
    if (role === 'doctor') return <Navigate to="/doctor/dashboard" replace />;
    if (role === 'staff') {
      const firstPage = getStaffFirstPage(user?.permissions || {});
      return <Navigate to={firstPage || '/doctor/dashboard'} replace />;
    }
    if (role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  }
  return children;
}


function DoctorRoute({ children, perm }) {
  const { isLoggedIn, role, user, isHydrated } = useAuth();
  const location = useLocation();
  if (!isHydrated) return <AppLoader />;
  if (!isLoggedIn || (role !== 'doctor' && role !== 'staff')) {
    // Pass current path as 'from' so login page can redirect back after login
    return <Navigate to="/doctor/login" state={{ from: location.pathname }} replace />;
  }

  if (role === 'staff') {
    const perms = user?.permissions || {};
    const firstPage = getStaffFirstPage(perms);

    // Staff always goes to staff-dashboard first (their own portal)
    if (!perm) {
      return children; // StaffDashboard handles permission display itself
    }

    // Staff: always block profile, staff management, earnings
    if (perm === 'profile' || perm === 'staff' || perm === 'earnings') {
      return <Navigate to={firstPage || '/doctor/login'} replace />;
    }

    // Staff: check specific permission
    const allowed = perms[perm] ?? false;
    if (!allowed) {
      return <Navigate to={firstPage || '/doctor/login'} replace />;
    }
  }

  return children;
}



function DoctorGuestRoute({ children }) {
  const { isLoggedIn, role, user, isHydrated, isProfileComplete } = useAuth();
  if (!isHydrated) return <AppLoader />;
  if (isLoggedIn) {
    // Patient visiting doctor login page → send to patient home
    if (role === 'patient') return <Navigate to={isProfileComplete ? '/' : '/setup-profile'} replace />;
    // Admin visiting doctor login → send to admin dashboard
    if (role === 'admin') return <Navigate to="/admin/dashboard" replace />;
    // Doctor/staff → send to their dashboard
    if (role === 'staff') {
      const firstPage = getStaffFirstPage(user?.permissions || {});
      return <Navigate to={firstPage || '/doctor/dashboard'} replace />;
    }
    return <Navigate to="/doctor/dashboard" replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { isLoggedIn, role, isHydrated } = useAuth();
  if (!isHydrated) return <AppLoader />;
  if (!isLoggedIn || role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AdminGuestRoute({ children }) {
  const { isLoggedIn, role, isHydrated } = useAuth();
  if (!isHydrated) return <AppLoader />;
  if (isLoggedIn && role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRoutes() {
  const { isLoggedIn } = useAuth();
  const [showSplash, setShowSplash] = React.useState(
    () => !sessionStorage.getItem('splashShown')
  );
  const handleSplashDone = React.useCallback(() => {
    sessionStorage.setItem('splashShown', '1');
    setShowSplash(false);
  }, []);
  return (
    <SocketProvider isLoggedIn={isLoggedIn}>
    <CallProvider>
      {showSplash && <SplashScreen onDone={handleSplashDone} />}
      <OfflineBanner />
      <GlobalCallOverlay />
      <RealtimeToast />
      <ScrollToTop />
      <Routes>
        {/* ── Public ─────────────────────────────────────────── */}
        <Route path="/"       element={<HomePage />} />
        <Route path="/search" element={<SearchPage />} />

        {/* ── Patient auth ────────────────────────────────────── */}
        <Route path="/login"
          element={<GuestRoute><PatientLoginPage /></GuestRoute>} />
        <Route path="/setup-profile"
          element={<PatientOnlyRoute><ProfileSetupPage /></PatientOnlyRoute>} />

        {/* ── Patient protected ────────────────────────────────── */}
        <Route path="/book/:doctorId"
          element={<PatientRoute><BookingPage /></PatientRoute>} />
        <Route path="/payment/cashfree/return"
          element={<PatientRoute><PaymentReturnPage /></PatientRoute>} />
        <Route path="/booking-success"
          element={<PatientRoute><BookingSuccessPage /></PatientRoute>} />
        <Route path="/appointments"
          element={<PatientOnlyRoute><MyAppointmentsPage /></PatientOnlyRoute>} />
        <Route path="/appointments/:id"
          element={<PatientOnlyRoute><AppointmentDetailPage /></PatientOnlyRoute>} />
        <Route path="/chats"
          element={<PatientOnlyRoute><ChatListPage /></PatientOnlyRoute>} />
        <Route path="/chat/:conversationId"
          element={<PatientOnlyRoute><ChatPage /></PatientOnlyRoute>} />
        <Route path="/medicine-chat/:doctorId"
          element={<PatientOnlyRoute><MedicineChatPage /></PatientOnlyRoute>} />
        <Route path="/scan-qr"
          element={<QRScannerPage />} />

        {/* ── Doctor ──────────────────────────────────────────── */}
        <Route path="/doctor/login"
          element={<DoctorGuestRoute><DoctorLoginPage /></DoctorGuestRoute>} />
        <Route path="/doctor/dashboard"
          element={<DoctorRoute><DoctorDashboard /></DoctorRoute>} />
        <Route path="/doctor/staff-dashboard"
          element={<DoctorRoute><DoctorDashboard /></DoctorRoute>} />
        <Route path="/doctor/appointments"
          element={<DoctorRoute perm="appointments"><DoctorAppointments /></DoctorRoute>} />
        <Route path="/doctor/slots"
          element={<DoctorRoute perm="slotManagement"><DoctorSlots /></DoctorRoute>} />
        <Route path="/doctor/profile"
          element={<DoctorRoute perm="profile"><DoctorProfileSetup /></DoctorRoute>} />
        <Route path="/doctor/earnings"
          element={<DoctorRoute perm="earnings"><DoctorEarnings /></DoctorRoute>} />
        <Route path="/doctor/chats"
          element={<DoctorRoute perm="chat"><DoctorChatList /></DoctorRoute>} />
        <Route path="/doctor/chat/:conversationId"
          element={<DoctorRoute perm="chat"><DoctorChatPage /></DoctorRoute>} />
        <Route path="/doctor/medicine-chat/:conversationId"
          element={<DoctorRoute perm="chat"><DoctorMedicineChatPage /></DoctorRoute>} />
        <Route path="/doctor/medicine-store-chats"
          element={<DoctorRoute perm="chat"><DoctorMedicineStoreChatList /></DoctorRoute>} />
        <Route path="/doctor/staff"
          element={<DoctorRoute perm="staff"><DoctorStaff /></DoctorRoute>} />
        <Route path="/doctor/my-qr"
          element={<DoctorRoute perm="profile"><DoctorQRPage /></DoctorRoute>} />
        {/* /doctor/:id AFTER all /doctor/word routes */}
        <Route path="/doctor/:id" element={<DoctorProfilePage />} />

        {/* ── Admin ───────────────────────────────────────────── */}
        <Route path="/admin/dashboard"
          element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/payments"
          element={<AdminRoute><AdminPayments /></AdminRoute>} />
        <Route path="/admin/doctors"
          element={<AdminRoute><AdminDoctors /></AdminRoute>} />
        <Route path="/admin/patients"
          element={<AdminRoute><AdminPatients /></AdminRoute>} />
        <Route path="/admin/appointments"
          element={<AdminRoute><AdminAppointments /></AdminRoute>} />
        <Route path="/admin/settings"
          element={<AdminRoute><AdminSettings /></AdminRoute>} />
        <Route path="/admin/revenue"
          element={<AdminRoute><AdminRevenue /></AdminRoute>} />
          <Route path="/admin/staff"
          element={<AdminRoute><AdminStaffPage /></AdminRoute>} />
        {/* /admin/:slug/login AFTER all /admin/word routes */}
        <Route path="/admin/:slug/login"
          element={<AdminGuestRoute><AdminLoginPage /></AdminGuestRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </CallProvider>
    </SocketProvider>
  );
}

export default function App() {
  return (
    <AppSettingsProvider>
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppRoutes />
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3500,
              style: { background:'#0F172A', color:'#fff', fontSize:'14px', borderRadius:'12px', padding:'12px 16px' },
              success: { iconTheme: { primary:'#10B981', secondary:'#fff' } },
              error:   { iconTheme: { primary:'#EF4444', secondary:'#fff' } },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </AppSettingsProvider>
  );
}
